/**
 * Email preferences API service
 *
 * Provides methods for coaches to manage their daily email summary preferences.
 */

import { getApiUrl } from '../config/api';
import { safeJsonParse } from '../utils/apiUtils';

export interface EmailPreferences {
  daily_email_enabled: boolean;
  daily_email_time: string; // HH:MM:SS format
}

export interface EmailPreferencesResponse extends EmailPreferences {
  message?: string;
}

/**
 * Get the current user's email preferences
 */
export const getEmailPreferences = async (): Promise<EmailPreferences> => {
  const token = localStorage.getItem('access');

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(getApiUrl('/api/users/me/email-preferences/'), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const result = await safeJsonParse<EmailPreferences>(response, 'fetching email preferences');
  
  if (!result.success) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    throw new Error(result.error || `Failed to fetch email preferences: ${response.statusText}`);
  }

  return result.data!;
};

/**
 * Update the current user's email preferences
 */
export const updateEmailPreferences = async (
  preferences: Partial<EmailPreferences>
): Promise<EmailPreferencesResponse> => {
  const token = localStorage.getItem('access');

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(getApiUrl('/api/users/me/email-preferences/'), {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(preferences),
  });

  const result = await safeJsonParse<EmailPreferencesResponse>(response, 'updating email preferences');
  
  if (!result.success) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    if (response.status === 403) {
      throw new Error('Only coaches can configure email preferences.');
    }
    throw new Error(result.error || `Failed to update email preferences: ${response.statusText}`);
  }

  return result.data!;
};

/**
 * Send a test email digest to the current user
 */
export const sendTestEmail = async (): Promise<{message: string; task_id: string; note: string}> => {
  const token = localStorage.getItem('access');

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(getApiUrl('/api/users/me/send-test-email/'), {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const result = await safeJsonParse<{message: string; task_id: string; note: string}>(response, 'sending test email');
  
  if (!result.success) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    if (response.status === 403) {
      throw new Error('Only coaches can send test emails.');
    }
    throw new Error(result.error || `Failed to send test email: ${response.statusText}`);
  }

  return result.data!;
};
